import React, { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { Loader } from "rsuite";
import { axInst } from "../Login/Service.jsx";
import { useSelector } from "react-redux";
import ManPowerDealerReviewFilter from "../Filters/ManPowerDealerReviewFilter.jsx";

const LEVEL_CONFIG = {
  REGION: {
    endpoint: "dealer-review/rm_manpower_new",
    payloadKey: "new_region",
    next: "RM",
  },
  RM: {
    endpoint: "dealer-review/area_manager_manpower_new",
    payloadKey: "rm",
    next: "AREA",
  },
  AREA: {
    endpoint: "dealer-review/tsm_name_manpower_new",
    payloadKey: "area_manager",
    next: "TSM",
  },
  TSM: {
    endpoint: "dealer-review/dealer_code_manpower_new",
    payloadKey: "tsm_name",
    next: null,
  },
};

//  API

const fetchLevelData = async (endpoint, payload) => {
  try {
    const res = await axInst.post(endpoint, payload);
    return Array.isArray(res.data) ? res.data : [];
  } catch (err) {
    console.error("API Error:", err);
    return [];
  }
};

/* ====================================================== */

const HierarchyPanel = ({ level, rowData, filters }) => {
  const config = LEVEL_CONFIG[level];
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      if (!config?.endpoint) {
        setLoading(false);
        return;
      }

      const payloadValue = rowData[Object.keys(rowData)[0]];

      if (!payloadValue) {
        console.warn("Missing payload value for level:", level);
        setLoading(false);
        return;
      }

      // ✅ COMBINED PAYLOAD (filters + hierarchy)
      const payload = {
        ...(filters || {}),
        [config.payloadKey]: [payloadValue],
      };

      const res = await fetchLevelData(config.endpoint, payload);
      setData(res);
      setLoading(false);
    };

    loadData();
  }, [level, rowData, config, filters]);

  const columns = useMemo(() => {
    if (!data.length) return [];
    return Object.keys(data[0]).map((key) => ({
      accessorKey: key,
      header: key.replace(/_/g, " "),
    }));
  }, [data]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableExpanding: !!config?.next,
    enableExpanderColumn: !!config?.next,
    getRowCanExpand: () => !!config?.next,

    // ✅ PASS FILTERS DOWN THE TREE
    renderDetailPanel: ({ row }) =>
      config?.next ? (
        <HierarchyPanel
          level={config.next}
          rowData={row.original}
          filters={filters}
        />
      ) : null,

    enablePagination: false,
    enableTopToolbar: false,
    enableBottomToolbar: false,
    initialState: { density: "compact" },
    muiTableBodyCellProps: { sx: { fontSize: "11px" } },
    muiTableHeadCellProps: {
      sx: {
        "& .Mui-TableHeadCell-Content-Wrapper": {
          whiteSpace: "nowrap!important",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        color: "white",
        fontSize: "11px",
      },
    },
  });

  if (loading) return <div className="p-2 text-sm">Loading...</div>;
  if (!data.length) return <div className="p-2">No data</div>;

  return <MaterialReactTable table={table} />;
};

/* ======================================================
   MAIN MODAL (TOP LEVEL = REGION)
====================================================== */
const ModalMuiTable = ({
  open,
  onClose,
  apiEndpoint, // REGION API
  heading = "Manpower Hierarchy",
}) => {
  const { data: body, submitted } = useSelector(
    (e) => e.filtersManpowerDealerReview
  );
  const { data = [], isLoading } = useQuery({
    queryKey: ["region-data", open, submitted],
    queryFn: async () => {
      const res = await axInst.post(apiEndpoint, body);
      return Array.isArray(res.data) ? res.data : [];
    },
    enabled: open,
  });

  // Escape key to close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [open, onClose]);

  const columns = useMemo(() => {
    if (!data.length) return [];
    return Object.keys(data[0]).map((key) => ({
      accessorKey: key,
      header: key.replace(/_/g, " "),
    }));
  }, [data]);

  const table = useMaterialReactTable({
    columns,
    data,
    enableExpanding: true,
    enableExpanderColumn: true,
    getRowCanExpand: () => true,
    renderDetailPanel: ({ row }) => (
      <HierarchyPanel level="REGION" rowData={row.original} filters={body} />
    ),
    initialState: { density: "compact" },
    muiTableHeadCellProps: {
      sx: {
        "& .Mui-TableHeadCell-Content-Wrapper": {
          whiteSpace: "nowrap!important",
          overflow: "hidden",
          textOverflow: "ellipsis",
        },
        color: "white",
        fontSize: "12px",
      },
    },
  });

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center ">
      <div className="bg-white w-[95%] h-[500px] max-w-6xl rounded-lg shadow-lg p-2 relative overflow-auto">
        <div className="text-lg font-semibold mb-2">{heading}</div>

        <button
          onClick={onClose}
          className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#2a5775] text-white flex items-center justify-center"
        >
          ×
        </button>
        <ManPowerDealerReviewFilter />

        {isLoading ? (
          <div className="p-4 text-center">
            <Loader />
          </div>
        ) : (
          <MaterialReactTable table={table} />
        )}
      </div>
    </div>
  );
};

export default ModalMuiTable;
